import { HStack } from '@chakra-ui/react';
import { FaThumbsDown, FaThumbsUp } from 'react-icons/fa';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomButton, CustomText } from '../../atoms';
import { appInsights } from '../../../appInsights';
// import { trackEvent } from '../../../Analytics/ga';
import { log } from '../../../utils';
import { useSettings } from '../../../stateManagement/SettingsContext';

interface ArticleFeedbackProps {
  sectionIdForFaq?: string;
  id: string;
  publishId: string;
  title: string;
  feedbackText: string;
  language: string;
}

export const ArticleFeedback: React.FC<ArticleFeedbackProps> = ({
  sectionIdForFaq,
  id,
  publishId,
  title,
  feedbackText,
  language,
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null);
  const { settings } = useSettings(); // assume this exists in your project
  const location = useLocation(); // current route

  // tiny debounce between clicks so user can't spam UI toggles
  const lastClickRef = useRef<number>(0);
  const DEBOUNCE_MS = 300;

  // Deferred analytics: wait this long before sending (if user doesn't change mind)
  const ANALYTICS_DELAY_MS = 30_000; // 30 seconds

  // timer + pending feedback refs
  const analyticsTimerRef = useRef<number | null>(null);
  const pendingFeedbackRef = useRef<null | {
    feedback: boolean;
    feedback_id: string;
    publish_Id?: string;
    feedback_title: string;
    feedback_language?: string;
    feedback_sectionIdForFaq?: string;
    timestamp: number;
  }>(null);

  // ---- Helper: actually send logging & telemetry ----
  const sendFeedbackToServer = (payload: {
    feedback: boolean;
    feedback_id: string;
    publish_Id?: string;
    feedback_title: string;
    feedback_language?: string;
    feedback_sectionIdForFaq?: string;
  }) => {
    // Use the same logic you had in onClickArticleFeedback but only when we want to SEND
    log(
      settings?.configs.showLogs,
      'articlefeedback',
      payload.feedback,
      payload.feedback_title,
      payload.feedback_id,
      payload.publish_Id,
      payload.feedback_sectionIdForFaq,
    );

    appInsights.trackEvent({
      name: 'landingPage',
      properties: {
        buttonName: 'relatedArticleTileFeedback',
        userName: 'anonymous',
        articleId: payload.feedback_id,
        articlePublishId: payload.publish_Id,
        articleTitle: payload.feedback_title,
        feedback: payload.feedback,
        publishId: payload.publish_Id,
        sectionId: payload.feedback_sectionIdForFaq,
        language: payload.feedback_language,
      },
    });

    if (payload.feedback) {
      // trackEvent('article_likes', {
      //   page: 'landingPage',
      //   article_id: payload.feedback_id,
      //   article_publish_id: payload.publish_Id,
      //   article_title: payload.feedback_title,
      // });
    } else {
      // trackEvent('article_dislikes', {
      //   page: 'landingPage',
      //   article_id: payload.feedback_id,
      //   article_publish_id: payload.publish_Id,
      //   article_title: payload.feedback_title,
      // });
    }
  };

  // ---- Schedule analytics (start/reset timer) ----
  const scheduleAnalytics = (payload: NonNullable<typeof pendingFeedbackRef.current>) => {
    // clear existing timer
    if (analyticsTimerRef.current) {
      window.clearTimeout(analyticsTimerRef.current);
      analyticsTimerRef.current = null;
    }

    pendingFeedbackRef.current = payload;

    analyticsTimerRef.current = window.setTimeout(() => {
      const toSend = pendingFeedbackRef.current;
      if (toSend) {
        sendFeedbackToServer({
          feedback: toSend.feedback,
          feedback_id: toSend.feedback_id,
          publish_Id: toSend.publish_Id,
          feedback_title: toSend.feedback_title,
          feedback_language: toSend.feedback_language,
          feedback_sectionIdForFaq: toSend.feedback_sectionIdForFaq,
        });
        pendingFeedbackRef.current = null;
        analyticsTimerRef.current = null;
      }
    }, ANALYTICS_DELAY_MS);
  };

  // ---- Cancel pending analytics (call when user deselects) ----
  const cancelPendingAnalytics = () => {
    if (analyticsTimerRef.current) {
      window.clearTimeout(analyticsTimerRef.current);
      analyticsTimerRef.current = null;
    }
    pendingFeedbackRef.current = null;
  };

  // ---- UI click handler (toggles selection, doesn't send immediately) ----
  const handleClick = (feedback: boolean) => {
    const now = Date.now();
    if (now - lastClickRef.current < DEBOUNCE_MS) return;
    lastClickRef.current = now;

    // toggle: if clicking same button, deselect
    const newValue = feedbackGiven === feedback ? null : feedback;
    setFeedbackGiven(newValue);

    if (newValue === null) {
      // user deselected -> cancel pending analytics
      cancelPendingAnalytics();
    } else {
      // user selected (or switched selection) -> schedule analytics
      scheduleAnalytics({
        feedback: newValue,
        feedback_id: id,
        publish_Id: publishId,
        feedback_title: title,
        feedback_language: language,
        feedback_sectionIdForFaq: sectionIdForFaq,
        timestamp: Date.now(),
      });
    }
  };

  // ---- Flush pending analytics when route changes or unmount ----
  useEffect(() => {
    // when route changes, if there is pending feedback, send it immediately
    if (pendingFeedbackRef.current) {
      const toSend = pendingFeedbackRef.current;
      sendFeedbackToServer({
        feedback: toSend.feedback,
        feedback_id: toSend.feedback_id,
        publish_Id: toSend.publish_Id,
        feedback_title: toSend.feedback_title,
        feedback_language: toSend.feedback_language,
        feedback_sectionIdForFaq: toSend.feedback_sectionIdForFaq,
      });
      cancelPendingAnalytics();
    }
    // Reset UI selection on route change as before
    setFeedbackGiven(null);
    lastClickRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // also flush on unmount (component destroyed)
  useEffect(() => {
    return () => {
      if (pendingFeedbackRef.current) {
        const toSend = pendingFeedbackRef.current;
        sendFeedbackToServer({
          feedback: toSend.feedback,
          feedback_id: toSend.feedback_id,
          publish_Id: toSend.publish_Id,
          feedback_title: toSend.feedback_title,
          feedback_language: toSend.feedback_language,
          feedback_sectionIdForFaq: toSend.feedback_sectionIdForFaq,
        });
        cancelPendingAnalytics();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const thumbUpSelected = feedbackGiven === true;
  const thumbDownSelected = feedbackGiven === false;

  return (
    <HStack spacing={6} pt="4" alignItems="center">
      <CustomText variant="h3" fontWeight="bold">
        {feedbackText}
      </CustomText>

      <CustomButton
        onClick={() => handleClick(true)}
        colorScheme="green"
        icon={<FaThumbsUp />}
        aria-pressed={thumbUpSelected}
        variant={thumbUpSelected ? 'solid' : 'outline'}
        bg={thumbUpSelected ? 'green.500' : 'transparent'}
        borderColor={thumbUpSelected ? 'green.500' : undefined}
        // visual prominence: selected full opacity, unselected slightly muted
        style={{
          opacity: thumbUpSelected ? 1 : 0.55,
          transform: thumbUpSelected ? 'scale(1.03)' : 'none',
          transition: 'all 160ms ease',
        }}
      />

      <CustomButton
        onClick={() => handleClick(false)}
        colorScheme="red"
        icon={<FaThumbsDown />}
        aria-pressed={thumbDownSelected}
        variant={thumbDownSelected ? 'solid' : 'outline'}
        bg={thumbDownSelected ? 'red.500' : 'transparent'}
        borderColor={thumbDownSelected ? 'red.500' : undefined}
        style={{
          opacity: thumbDownSelected ? 1 : 0.55,
          transform: thumbDownSelected ? 'scale(1.03)' : 'none',
          transition: 'all 160ms ease',
        }}
      />
    </HStack>
  );
};
